import isNil from 'lodash/isNil'
import {
  InvitationStatus,
  UserDisplayStatus,
  UserListRow,
  UserRbacRole,
} from '../../api/dto/user-invitation.dto'
import { isInviteAllowedRbacRoleName } from './inviteRbacRoles'

/** The funding roles this row explicitly holds, i.e. what a removal would take. */
export const getRemovableRoles = (row: UserListRow): UserRbacRole[] =>
  (row.rbacRoles ?? []).filter((role) => isInviteAllowedRbacRoleName(role.name))

/**
 * A live activation link. The roles such a row reports are staged behind the
 * link rather than granted, so they are the invitation's to clear, not a
 * removal's.
 */
const hasLiveInvitation = (row: UserListRow): boolean =>
  !isNil(row.userId) && row.invitationStatus === InvitationStatus.Pending

export interface AccessRemovalTarget {
  row: UserListRow
  /**
   * Funding roles the row actually holds, which come off. Empty until the
   * account is active: before that the roles are staged on the invitation and
   * grant nothing, so a removal has none to take.
   */
  roles: UserRbacRole[]
  /** A live activation link exists, so it is withdrawn rather than left usable. */
  hasLiveInvitation: boolean
}

export interface AccessRemovalPlan {
  /**
   * Everyone selected. A row is on the list because it receives invoices or
   * holds the role, so there is always something to take and none can be
   * skipped here.
   */
  targets: AccessRemovalTarget[]
  /** Rows whose activation link is withdrawn. */
  invitationCount: number
  /** Rows losing at least one funding role. */
  roleCount: number
  /** Companies the selection stops being invoiced for. */
  companyNames: string[]
}

export const EMPTY_ACCESS_REMOVAL_PLAN: AccessRemovalPlan = {
  targets: [],
  invitationCount: 0,
  roleCount: 0,
  companyNames: [],
}

/**
 * Describes what taking funding access away would do to each selected row: the
 * activation link, the invoices, the companies and the roles all go together.
 *
 * A preview built from list data the client already has, so it can be stale.
 * The server re-checks live state and its results are what count.
 */
export const buildAccessRemovalPlan = (
  rows: UserListRow[]
): AccessRemovalPlan => {
  const targets = rows.map((row): AccessRemovalTarget => {
    const invited = hasLiveInvitation(row)
    const roles =
      invited || row.displayStatus !== UserDisplayStatus.Active
        ? []
        : getRemovableRoles(row)
    return { row, roles, hasLiveInvitation: invited }
  })

  return {
    targets,
    invitationCount: targets.filter((target) => target.hasLiveInvitation)
      .length,
    roleCount: targets.filter((target) => target.roles.length > 0).length,
    companyNames: rows
      .flatMap((row) => row.billingClientNames)
      .filter((name, index, all) => all.indexOf(name) === index),
  }
}
